// /frontend/src/pages/checkout/CheckoutSteps.jsx - Modified for RFQ
import React from 'react';
import { Link } from 'react-router-dom';
import { isRFQModeEnabled } from '../../config/rfqConfig';

const CheckoutSteps = ({ step, orderId = null }) => {
  const isRFQMode = isRFQModeEnabled();
  
  // Different step configurations based on RFQ mode
  const steps = isRFQMode ? [
    { id: 'shipping', label: 'Shipping' },
    { id: 'placeorder', label: 'Request Quote' },
    { id: 'confirmation', label: 'Quote Submitted' }
  ] : [
    { id: 'shipping', label: 'Shipping' },
    { id: 'placeorder', label: 'Place Order' },
    { id: 'confirmation', label: 'Confirmation' },
    { id: 'payment', label: 'Payment' }
  ];
  
  // Function to get the correct URL based on step
  const getStepUrl = (stepId) => {
    switch (stepId) {
      case 'shipping':
        return '/shipping';
      case 'placeorder':
        return '/placeorder';
      case 'confirmation':
        return orderId ? `/order/confirmation/${orderId}` : '#';
      case 'payment':
        // In RFQ mode, payment step doesn't exist or redirects to confirmation
        return isRFQMode 
          ? (orderId ? `/order/confirmation/${orderId}` : '#')
          : (orderId ? `/payment/${orderId}` : '#');
      default:
        return '#';
    }
  };

  // Function to determine if step should be clickable
  const isStepClickable = (stepId, stepIndex) => {
    const currentStepIndex = steps.findIndex(x => x.id === step);
    const isCompleted = currentStepIndex > stepIndex;
    const hasValidUrl = getStepUrl(stepId) !== '#';
    
    // In RFQ mode, don't allow clicking on payment step if it exists
    if (isRFQMode && stepId === 'payment') {
      return false;
    }
    
    return isCompleted && hasValidUrl;
  };

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center">
        {steps.map((s, index) => {
          // Determine if step is active, completed, or upcoming
          const isActive = s.id === step;
          const isCompleted = steps.findIndex(x => x.id === step) > index;
          const isClickable = isStepClickable(s.id, index);
          
          return (
            <React.Fragment key={s.id}>
              {/* Step circle */}
              {isClickable ? (
                <Link 
                  to={getStepUrl(s.id)}
                  className={`flex items-center justify-center w-10 h-10 rounded-full 
                    ${isActive 
                      ? (isRFQMode ? 'bg-blue-600 text-white' : 'bg-teal-800 text-white')
                      : (isRFQMode ? 'bg-blue-200 text-blue-800' : 'bg-teal-200 text-teal-800')
                    }
                    ${isCompleted 
                      ? (isRFQMode ? 'bg-blue-700 text-white' : 'bg-teal-700 text-white') 
                      : ''
                    }
                    hover:opacity-80 transition-opacity
                  `}
                >
                  {index + 1}
                </Link>
              ) : (
                <div 
                  className={`flex items-center justify-center w-10 h-10 rounded-full 
                    ${isActive 
                      ? (isRFQMode ? 'bg-blue-600 text-white' : 'bg-teal-800 text-white')
                      : 'bg-gray-200 text-gray-500'
                    }
                    ${isCompleted 
                      ? (isRFQMode ? 'bg-blue-700 text-white' : 'bg-teal-700 text-white') 
                      : ''
                    }
                  `}
                >
                  {isCompleted ? (
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
              )}
              
              {/* Step label */}
              <div className={`hidden sm:block text-sm font-medium ${
                isActive 
                  ? (isRFQMode ? 'text-blue-800' : 'text-teal-800')
                  : isCompleted 
                    ? (isRFQMode ? 'text-blue-700' : 'text-teal-700')
                    : 'text-gray-500'
              }`}>
                {s.label}
              </div>
              
              {/* Connecting line between steps (except after the last step) */}
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-2 ${
                  isCompleted 
                    ? (isRFQMode ? 'bg-blue-600' : 'bg-teal-600')
                    : 'bg-gray-200'
                }`}></div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Mobile step labels */}
      <div className="flex justify-between mt-2 sm:hidden">
        {steps.map((s) => (
          <div 
            key={`label-${s.id}`}
            className={`text-xs font-medium ${
              s.id === step 
                ? (isRFQMode ? 'text-blue-800' : 'text-teal-800')
                : 'text-gray-500'
            }`}
          >
            {s.label}
          </div>
        ))}
      </div>
      
      {/* RFQ Mode Indicator */}
      {isRFQMode && (
        <div className="mt-4 text-center">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Request for Quotation Mode
          </span>
        </div>
      )}
    </div>
  );
};

export default CheckoutSteps;